/**
 * Bean Snake — shared rules surface.
 *
 * PURE MODULE. No DOM, no Math.random, no Date.now. The client renders this
 * engine and the server replays it; any impurity here lets the two disagree,
 * which is the one failure mode the whole anti-cheat design exists to prevent.
 */

export const GRID = 20;
export const MAX_TICKS = 6000;

/** Bumped only on a rule change, and only landing on a Monday: the verifier
 *  refuses logs whose version differs, so a mid-week bump would re-score a
 *  live board with new physics. */
export const ENGINE_VERSION = 1;

export type Direction = 'up' | 'down' | 'left' | 'right';
export type Roast = 'green' | 'medium' | 'dark';

export interface RoastSpec {
  points: number;
  growth: number;
  /** Ticks before the bean rerolls. null = never expires (green only). */
  ttl: number | null;
}

/** Dark is deliberately not free upside: +3 segments makes the REST of the run
 *  harder. That trade is what makes the board a skill contest. */
export const ROASTS: Record<Roast, RoastSpec> = {
  green: { points: 1, growth: 1, ttl: null },
  medium: { points: 3, growth: 2, ttl: 60 },
  dark: { points: 5, growth: 3, ttl: 40 },
};

export interface Cell { x: number; y: number; }

export interface Bean extends Cell {
  roast: Roast;
  /** Absolute tick at which this bean rerolls; null for green. */
  expiresAtTick: number | null;
}

export interface GameState {
  tick: number;
  /** Head first. */
  snake: Cell[];
  dir: Direction;
  /** Segments still owed from beans already eaten. */
  pendingGrowth: number;
  bean: Bean;
  score: number;
  dead: boolean;
  /** RNG lives INSIDE state so a state is fully self-describing and replay
   *  cannot drift from the live run. */
  rngState: number;
}

/** `[tick, direction]` — what the client records and submits instead of a score. */
export type InputEvent = [number, Direction];
